import { locationGreetings, defaultGreeting } from './greetings.js';

// Constants
const VAD_CONFIG = {
    silenceThreshold: -50,
    silenceDuration: 1500,
    voiceThreshold: -35,
    minSpeechDuration: 250
};

// State variables
let audioContext;
let analyser;
let mediaRecorder;
let audioChunks = [];
let isSessionActive = false;
let isRecording = false;
let isProcessing = false;
let silenceStart = null;
let speechStart = null;
let selectedLanguage = 'en-US';
let pendingSuggestions = null;
let currentDisplayOperation = null;
let isAnimationActive = false;

// Chat history initialization
let chatHistory = [
    { role: 'user', content: 'dummy message just to start the conversation' },
    { role: 'assistant', content: getGreetingMessage() }
];

function getGreetingMessage() {
    const urlParams = new URLSearchParams(window.location.search);
    const location = urlParams.get('location');
    return locationGreetings[location] || defaultGreeting;
}

// DOM Elements
const startMicButton = document.getElementById('start-mic');
const sendMessageButton = document.getElementById('send-message');
const chatInput = document.getElementById('chat-input');
const languageSelect = document.getElementById('language-select');

// Event Listeners
document.addEventListener('DOMContentLoaded', function() {
    updateTranscript('assistant', chatHistory[1].content);
    updateSuggestions();
});
startMicButton.addEventListener('click', handleMicClick);
sendMessageButton.addEventListener('click', handleSendMessage);
chatInput.addEventListener('keypress', (event) => {
    if (event.key === 'Enter') {
        handleSendMessage();
    }
});
languageSelect.addEventListener('change', (event) => {
    selectedLanguage = event.target.value;
});

languageSelect.addEventListener('change', function() {
    const selectedOption = this.options[this.selectedIndex];
    const code = selectedOption.text.split(' - ')[0];
    selectedOption.setAttribute('selected', true);
    this.size = 1;
});

// Initial setup to show only the code
document.addEventListener('DOMContentLoaded', function() {
    const select = document.getElementById('language-select');
    const selectedOption = select.options[select.selectedIndex];
    const code = selectedOption.text.split(' - ')[0];
    selectedOption.setAttribute('selected', true);
});

function handleMicClick() {
    if (!isSessionActive) {
        startVADSession();
    } else {
        stopVADSession();
    }
}

function startVADSession() {
    navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
            audioContext = new AudioContext();
            analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            
            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.8;

            window.activeStream = stream;
            
            startMicButton.innerHTML = '⏹️';
            startMicButton.classList.add('recording');
            
            isSessionActive = true;
            
            detectVoiceActivity();
        })
        .catch(error => {
            console.error('Error accessing microphone:', error);
            alert('Microphone access is required for this feature.');
        });
}

function detectVoiceActivity() {
    if (!isSessionActive) return;

    const dataArray = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(dataArray);

    const rms = Math.sqrt(dataArray.reduce((acc, val) => acc + val * val, 0) / dataArray.length);
    const db = 20 * Math.log10(rms);

    if (db > VAD_CONFIG.voiceThreshold) {
        if (!isRecording) {
            startNewRecording();
        }
        silenceStart = null;
        speechStart = speechStart || Date.now();
    } else if (db < VAD_CONFIG.silenceThreshold) {
        if (isRecording) {
            if (!silenceStart) {
                silenceStart = Date.now();
            } else if (Date.now() - silenceStart > VAD_CONFIG.silenceDuration) {
                if (Date.now() - speechStart > VAD_CONFIG.minSpeechDuration) {
                    stopCurrentRecording();
                }
            }
        }
    }

    updateVADIndicator(db);
    requestAnimationFrame(detectVoiceActivity);
}

function startNewRecording() {
    if (!window.activeStream) return;
    
    mediaRecorder = new MediaRecorder(window.activeStream);
    audioChunks = [];
    
    mediaRecorder.addEventListener('dataavailable', event => {
        audioChunks.push(event.data);
    });

    mediaRecorder.addEventListener('stop', () => {
        if (audioChunks.length > 0) {
            const audioBlob = new Blob(audioChunks, { type: 'audio/webm;codecs=opus' });
            sendAudioForTranscription(audioBlob);
        }
    });

    mediaRecorder.start();
    isRecording = true;
    startMicButton.classList.add('active-recording');
}

function stopCurrentRecording() {
    if (mediaRecorder && mediaRecorder.state === "recording") {
        mediaRecorder.stop();
        isRecording = false;
        isProcessing = true;
        speechStart = null;
        silenceStart = null;
        startMicButton.classList.remove('active-recording');
        startMicButton.classList.add('processing');
    }
}

function stopVADSession() {
    isSessionActive = false;
    isProcessing = false;
    
    if (isRecording) {
        stopCurrentRecording();
    }
    
    if (window.activeStream) {
        window.activeStream.getTracks().forEach(track => track.stop());
        window.activeStream = null;
    }
    
    if (audioContext) {
        audioContext.close();
        audioContext = null;
        analyser = null;
    }
    
    startMicButton.innerHTML = '🎤';
    startMicButton.classList.remove('recording');
    startMicButton.classList.remove('active-recording');
    startMicButton.classList.remove('processing');
}

function updateVADIndicator(db) {
    const normalizedLevel = Math.max(0, Math.min(1, (db - VAD_CONFIG.silenceThreshold) / 
        (VAD_CONFIG.voiceThreshold - VAD_CONFIG.silenceThreshold)));
    
    if (isProcessing) {
        startMicButton.style.borderColor = 'rgba(255, 165, 0, 0.8)';
    } else if (isRecording) {
        startMicButton.style.borderColor = `rgba(255, ${Math.floor(normalizedLevel * 255)}, 0, 0.8)`;
    } else {
        startMicButton.style.borderColor = `rgba(121, ${Math.floor(normalizedLevel * 255)}, 187, 0.8)`;
    }
}

function sendAudioForTranscription(audioBlob) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('languageCode', selectedLanguage);

    fetch('/.netlify/functions/transcribeAudio', {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.transcript && data.transcript.trim()) {
            const message = data.transcript;
            updateTranscript('user', message);
            chatHistory.push({ role: 'user', content: message });
            sendMessageToAnthropic(message);
        }
    })
    .catch(error => {
        console.error('Error sending audio for transcription:', error);
    })
    .finally(() => {
        isProcessing = false;
        startMicButton.classList.remove('processing');
    });
}

function sendMessageToAnthropic(message) {
    console.log('Sending message:', message);

    stopOngoingAnimations();
    document.dispatchEvent(new CustomEvent('assistantThinking'));

    fetch('/.netlify/functions/anthropicProxy', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            messages: chatHistory,
            model: 'claude-haiku-4-5-20251001',
            max_tokens: 1024,
        }),
    })
    .then(response => response.json())
    .then(data => {
        if (data.content) {
            const message = data.content[0].text;
            updateTranscript('assistant', message);
            chatHistory.push({ role: 'assistant', content: message });
            updateSuggestions();
        }
    })
    .catch(error => {
        console.error('Error:', error);
        updateTranscript('assistant', 'An error occurred while processing your message.');
    });
}

function handleSendMessage() {
    const message = chatInput.value.trim();
    if (message) {
        console.log('handleSendMessage - Processing message:', message);
        hideSuggestions();
        stopOngoingAnimations();
        
        updateTranscript('user', message);
        chatHistory.push({ role: 'user', content: message });
        chatInput.value = '';
        sendMessageToAnthropic(message);
    }
}

function updateTranscript(role, text) {
    const transcriptContainer = document.getElementById('transcript');
    const newElement = document.createElement('div');
    newElement.className = `transcript-entry ${role}`;
    
    if (role === 'assistant') {
        transcriptContainer.appendChild(newElement);
        displayGradually(text, newElement);
    } else {
        // For user messages, just display immediately without animation
        newElement.innerHTML = text;
        transcriptContainer.appendChild(newElement);
        transcriptContainer.scrollTop = transcriptContainer.scrollHeight;
    }

    // Keep message limit
    const maxMessages = 20;
    const messages = transcriptContainer.querySelectorAll('.transcript-entry');
    if (messages.length > maxMessages) {
        for (let i = 0; i < messages.length - maxMessages; i++) {
            transcriptContainer.removeChild(messages[i]);
        }
    }
}

function displayGradually(text, element) {
    console.log('Starting new gradual display');
    const wordsPerMinute = 500;
    const words = text.split(' ');
    let currentIndex = 0;
    let displayedText = '';
    let timeoutId = null;
    
    // Generate a unique ID for this animation
    const animationId = Date.now();
    console.log('Animation ID:', animationId);
    
    // Cancel any existing display operation
    if (currentDisplayOperation) {
        clearTimeout(currentDisplayOperation.timeoutId);
        currentDisplayOperation.element.innerHTML = currentDisplayOperation.displayedText;
        updateChatHistoryWithPartialMessage(currentDisplayOperation.displayedText);
    }
    
    isAnimationActive = true;
    
    function displayNextWord() {
        console.log('Display next word, isAnimationActive:', isAnimationActive);
        // Check if this animation should still be running
        if (!isAnimationActive || currentDisplayOperation?.animationId !== animationId) {
            console.log('Animation stopped, keeping partial text');
            element.innerHTML = displayedText;
            element.scrollIntoView({ behavior: 'smooth', block: 'end' });
            updateChatHistoryWithPartialMessage(displayedText);
            return;
        }
        
        if (currentIndex < words.length) {
            displayedText = displayedText + words[currentIndex] + ' ';
            element.innerHTML = displayedText;
            
            if (currentIndex === 0) {
                const startEvent = new CustomEvent('textUpdate', { 
                    detail: { text, sentenceEnded: false }
                });
                document.dispatchEvent(startEvent);
            }
            
            currentIndex++;
            element.scrollIntoView({ behavior: 'smooth', block: 'end' });
            
            const delay = (60 * 1000) / wordsPerMinute;
            timeoutId = setTimeout(displayNextWord, delay);
            
            currentDisplayOperation = {
                timeoutId: timeoutId,
                displayedText: displayedText,
                element: element,
                animationId: animationId
            };
        } else {
            isAnimationActive = false;
            currentDisplayOperation = null;
            const stopEvent = new CustomEvent('textUpdate', { 
                detail: { text: displayedText, sentenceEnded: true }
            });
            document.dispatchEvent(stopEvent);
            
            if (pendingSuggestions) {
                displaySuggestions(pendingSuggestions);
                pendingSuggestions = null;
            }
        }
    }

    timeoutId = setTimeout(displayNextWord, 50);
    currentDisplayOperation = {
        timeoutId: timeoutId,
        displayedText: '',
        element: element,
        animationId: animationId
    };
}

function updateSuggestions() {
    fetch('/.netlify/functions/getSuggestions', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            messages: chatHistory,
            language: selectedLanguage
        }),
    })
    .then(response => response.json())
    .then(responseData => {
        console.log('Raw response from Anthropic:', responseData);
        try {
            pendingSuggestions = JSON.parse(responseData.content[0].text);
            // Don't display suggestions here anymore
        } catch (error) {
            console.error('Error parsing suggestions:', error);
        }
    })
    .catch(error => {
        console.error('Error getting suggestions:', error);
    });
}

function displaySuggestions(suggestions) {
    const container = document.getElementById('suggestion-container');
    container.innerHTML = '';

    const personalBubble = createSuggestionBubble(suggestions.personal);
    const foodBubble = createSuggestionBubble(suggestions.food);

    container.appendChild(personalBubble);
    container.appendChild(foodBubble);
}

function createSuggestionBubble(text) {
    const bubble = document.createElement('div');
    bubble.className = 'suggestion-bubble';
    bubble.textContent = text;
    bubble.addEventListener('click', () => {
        chatInput.value = text;
        handleSendMessageFromSuggestion(text);
        hideSuggestions();
    });
    return bubble;
}

function handleSendMessageFromSuggestion(text) {
    if (text) {
        console.log('handleSendMessageFromSuggestion:', text);
        // Don't trigger animation here anymore
        updateTranscript('user', text);
        chatHistory.push({ role: 'user', content: text });
        chatInput.value = '';
        sendMessageToAnthropic(text);
    }
}

function hideSuggestions() {
    const container = document.getElementById('suggestion-container');
    container.innerHTML = '';
}

// Add this with your other event listeners
document.addEventListener('textUpdate', (event) => {
    const { text, sentenceEnded } = event.detail;
    console.log('textUpdate event received:', { text, sentenceEnded });
    // Here you can trigger whatever animation you want based on the text and sentenceEnded flag
    if (sentenceEnded) {
        // Trigger end-of-sentence animation
    }
    // Trigger character-by-character animation
});

function updateChatHistoryWithPartialMessage(partialText) {
    if (chatHistory.length > 0) {
        // Replace the last assistant message with the partial text
        const lastMessage = chatHistory[chatHistory.length - 1];
        if (lastMessage.role === 'assistant') {
            lastMessage.content = partialText.trim();
        }
    }
}

function stopOngoingAnimations() {
    console.log('Stopping animations...');
    // Force stop any ongoing animation
    isAnimationActive = false;
    
    if (currentDisplayOperation) {
        console.log('Current operation exists, clearing timeout:', currentDisplayOperation.timeoutId);
        // Clear the timeout immediately
        clearTimeout(currentDisplayOperation.timeoutId);
        
        // Keep only the text that was displayed so far
        const partialText = currentDisplayOperation.displayedText;
        console.log('Partial text:', partialText);
        currentDisplayOperation.element.innerHTML = partialText;
        updateChatHistoryWithPartialMessage(partialText);
        
        // Immediately nullify the current operation
        currentDisplayOperation = null;
    } else {
        console.log('No current operation to stop');
    }
}